import clsx from 'clsx';
import TriggerInput from './TriggerInput';
import TriggerButton from './TriggerButton';
import { ChevronDown } from '@/legacy/components/ui/Icons';
import { useSelect } from '../hooks/useCoreContext';
import { useTranslation } from 'react-i18next';
import { TriggerVariant } from '../types';
import { NonTranslatableText } from '@/shared/ui/NonTranslatableText';

type TriggerProps = React.HTMLAttributes<HTMLDivElement> & {
	fieldError?: React.ReactNode | string;
	variant?: TriggerVariant;
	placeholder: string;
	disabled?: boolean;
	displayValue?: React.ReactNode;
};

const Trigger = ({ fieldError, variant, placeholder, disabled, displayValue }: TriggerProps) => {
	const {
		open,
		ids,
		refs,
		setOpen,
		dontOpenDropdownIfNoOptions,
		options,
		inputValue,
		value,
		multiple,
		toggle,
	} = useSelect();
	const isCombobox = variant === 'combobox';
	const { t } = useTranslation();
	const selectedOption =
		!multiple && value !== null && (typeof value === 'string' || typeof value === 'number')
			? options.find((opt) => opt.value === value)
			: undefined;

	const focusInput = () => {
		const el = document.getElementById(ids.input) as HTMLInputElement | null;
		el?.focus();
	};

	const textToDisplay = displayValue
		? displayValue
		: selectedOption
			? (selectedOption.renderLabel ??
				(selectedOption.notTranslatable ? (
					<NonTranslatableText>{selectedOption.label}</NonTranslatableText>
				) : (
					selectedOption.label
				)))
			: placeholder;

	const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
		if (!isCombobox) e.preventDefault();

		if (disabled) return;
		if (isCombobox) {
			requestAnimationFrame(focusInput);
		}

		const isEmptyQuery = !inputValue || inputValue.trim() === '';
		const noResults = options.length === 0;

		const blockOpen = dontOpenDropdownIfNoOptions && isEmptyQuery && noResults && !open;

		if (blockOpen) return;
		setOpen(!open);
	};

	const onEnterDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (e.key !== 'Enter') return;
		const isEmptyQuery = !inputValue || inputValue.trim() === '';

		if (isCombobox && isEmptyQuery) {
			e.preventDefault();
			toggle('');
			setOpen(false);
		}
	};

	return (
		<div
			ref={refs.setReference}
			aria-controls={ids.listbox}
			aria-expanded={open}
			aria-labelledby={ids.label}
			className={clsx(
				'group gap-2',
				variant == 'round-button'
					? 'field-round-button'
					: variant == 'ghost'
						? ''
						: variant == 'menu-item'
							? 'flex items-center w-full'
							: 'field',
				{
					'field-error': fieldError,
					'field-border':
						!fieldError && !disabled && variant !== 'ghost' && variant !== 'menu-item',
					'border-cuaternary': open,
					'field-disabled': disabled,
				},
			)}
			onMouseDown={onMouseDown}
			onKeyDown={onEnterDown}
		>
			{isCombobox ? (
				<TriggerInput
					placeholder={t(placeholder)}
					className='flex-1 bg-transparent outline-none'
					disabled={disabled}
				/>
			) : (
				<TriggerButton
					text={typeof textToDisplay === 'string' ? t(textToDisplay) : textToDisplay}
					disabled={disabled}
					className={variant == 'round-button' ? 'text-secondary' : ''}
				/>
			)}
			{dontOpenDropdownIfNoOptions && options.length === 0 ? null : (
				<ChevronDown
					className={clsx(
						'ml-auto  transition-transform duration-200 group-aria-expanded:rotate-180',
						disabled ? 'opacity-50 cursor-not-allowed' : '',
						variant == 'ghost'
							? 'hidden'
							: variant == 'round-button'
								? 'text-secondary'
								: variant == 'menu-item'
									? 'text-icon-tertiary'
									: 'text-icon-cuaternary',
					)}
				/>
			)}
		</div>
	);
};

export default Trigger;
